import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { post, get } from '../utility/fetch';
import notification from '../utility/notification';
import logo from "../Assets/images/ConnectHealthPro.png";
import { ArrowBack } from '@mui/icons-material';
import SignupStepper from './SignupStepper';

const styles = {
    container: {
        display: 'flex',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        padding: '24px',
        boxSizing: 'border-box',
    },
    paper: {
        maxWidth: '500px',
        width: '100%',
        margin: 'auto',
        padding: '32px',
        borderRadius: '8px',
        boxShadow: '0px 3px 6px rgba(0,0,0,0.16)',
        backgroundColor: '#fff',
    },
    logoContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '32px',
    },
    logo: {
        height: '60px',
    },
    title: {
        textAlign: 'center',
        marginBottom: '12px',
        color: '#3c7e2d',
        fontSize: '28px',
        fontWeight: 'bold',
    },
    subtitle: {
        textAlign: 'center',
        marginBottom: '32px',
        color: '#666',
        fontSize: '14px',
    },
    email: {
        fontWeight: 'bold',
        color: '#3c7e2d',
    },
    otpContainer: {
        display: 'flex',
        justifyContent: 'center',
        gap: '12px',
        marginBottom: '24px',
    },
    otpInput: {
        width: '60px',
        height: '60px',
        fontSize: '24px',
        textAlign: 'center',
        border: '2px solid #ccc',
        borderRadius: '8px',
        outline: 'none',
        transition: 'border-color 0.3s',
    },
    otpInputFocused: {
        borderColor: '#3c7e2d',
    },
    button: {
        width: '100%',
        padding: '12px',
        backgroundColor: '#3c7e2d',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        fontSize: '16px',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'background-color 0.3s',
        marginBottom: '12px',
    },
    buttonDisabled: {
        backgroundColor: '#ccc',
        cursor: 'not-allowed',
    },
    secondaryButton: {
        width: '100%',
        padding: '12px',
        backgroundColor: 'transparent',
        color: '#3c7e2d',
        border: '1px solid #3c7e2d',
        borderRadius: '4px',
        fontSize: '16px',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'all 0.3s',
    },
    timerText: {
        textAlign: 'center',
        fontSize: '14px',
        color: '#666',
        marginBottom: '12px',
    },
    backButton: {
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: '#3c7e2d',
        cursor: 'pointer',
        border: 'none',
        background: 'none',
        fontSize: '16px',
    },
    iconContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '24px',
        fontSize: '64px',
    }
};

export default function OtpVerification() {
    const navigate = useNavigate();
    const location = useLocation();
    const email = location.state?.email;
    const userId = location.state?.userId;

    const [otp, setOtp] = useState(['', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [timer, setTimer] = useState(60);
    const [canResend, setCanResend] = useState(false);

    // Encouraging message
    const encouragingMessage = {
        title: "Almost There!",
    };

    useEffect(() => {
        if (!email) {
            notification({
                message: "Email not found. Please sign up again.",
                type: "error"
            });
            navigate('/create-account');
        }
    }, [email, navigate]);

    useEffect(() => {
        if (timer > 0) {
            const interval = setInterval(() => {
                setTimer(prev => prev - 1);
            }, 1000);
            return () => clearInterval(interval);
        } else {
            setCanResend(true);
        }
    }, [timer]);

    const handleChange = (index, value) => {
        if (/^\d?$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);

            // Auto-focus next input
            if (value && index < otp.length - 1) {
                document.getElementById(`otp-${index + 1}`).focus();
            }
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            document.getElementById(`otp-${index - 1}`).focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, 4);
        if (/^\d+$/.test(pastedData)) {
            const newOtp = pastedData.split('');
            setOtp([...newOtp, ...Array(4 - newOtp.length).fill('')]);
            
            // Focus the next empty input or the last input
            const nextIndex = Math.min(pastedData.length, 3);
            document.getElementById(`otp-${nextIndex}`).focus();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const otpCode = otp.join('');
        if (otpCode.length !== 4) {
            notification({
                message: "Please enter complete 4-digit OTP",
                type: "error"
            });
            return;
        }

        setLoading(true);

        try {
            const res = await get(`/auth/validateotp?otp=${otpCode}&email=${email}`);
            
            if (res === true || res) {
                notification({
                    message: "OTP verified successfully!",
                    type: "success"
                });
                navigate('/create-password', { 
                    state: { 
                        email: email,
                        userId: userId 
                    } 
                });
            } else {
                notification({
                    message: "Invalid OTP. Please try again.",
                    type: "error"
                });
            }
        } catch (error) {
            console.error('OTP verification error:', error);
            notification({
                message: "Error verifying OTP",
                type: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (!canResend) return;

        setLoading(true);
        try {
            const res = await post(`/Auth/generate-otp?email=${email}`);
            
            if (res?.status === "success") {
                notification({
                    message: "New OTP sent to your email",
                    type: "success"
                });
                setTimer(60);
                setCanResend(false);
                setOtp(['', '', '', '']);
            } else {
                notification({
                    message: "Failed to resend OTP. Please try again.",
                    type: "error"
                });
            }
        } catch (error) {
            console.error('Resend OTP error:', error);
            notification({
                message: "Failed to resend OTP",
                type: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const maskEmail = (email) => {
        if (!email) return '';
        const [username, domain] = email.split('@');
        const maskedUsername = username.slice(0, 2) + '****' + username.slice(-1);
        return `${maskedUsername}@${domain}`;
    };

    return (
        <div style={styles.container}>
            <div style={styles.paper}>
                <div style={styles.logoContainer}>
                    <img src={logo} alt="ConnectHealthPro" style={styles.logo} />
                </div>

                <SignupStepper currentStep={2} />


                <h1 style={styles.title}>{encouragingMessage.title}</h1>
                
                <p style={{ ...styles.subtitle, marginBottom: '24px' }}>
                    We've sent a 4-digit verification code to<br />
                    <span style={styles.email}>{maskEmail(email)}</span>
                </p>

                <form onSubmit={handleSubmit}>
                    <div style={styles.otpContainer}>
                        {otp.map((digit, index) => (
                            <input
                                key={index}
                                id={`otp-${index}`}
                                type="text"
                                maxLength={1}
                                value={digit}
                                onChange={(e) => handleChange(index, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                onPaste={handlePaste}
                                style={{
                                    ...styles.otpInput,
                                    ...(digit ? styles.otpInputFocused : {})
                                }}
                                autoFocus={index === 0}
                            />
                        ))}
                    </div>

                    <button
                        type="submit"
                        style={{
                            ...styles.button,
                            ...(loading || otp.join('').length !== 4 ? styles.buttonDisabled : {})
                        }}
                        disabled={loading || otp.join('').length !== 4}
                    >
                        {loading ? 'Verifying...' : 'Verify OTP'}
                    </button>
                </form>

                {timer > 0 ? (
                    <p style={styles.timerText}>
                        Resend OTP in {timer}s
                    </p>
                ) : (
                    <button
                        style={styles.secondaryButton}
                        onClick={handleResendOtp}
                        disabled={loading}
                    >
                        {loading ? 'Sending...' : 'Resend OTP'}
                    </button>
                )}
            </div>
        </div>
    );
}
